/**
 * Responsibility: Implement sheet contracts behavior for the ui/helpers surface.
 * Scope: Owns ui/helpers concerns only; cross-surface orchestration is excluded.
 */
import type { CompanionSheetData, DormantEffect, NpcSheetData, Vitals } from "@anyquest/shared-core"

export const SHEET_LIMITS = Object.freeze({
  text: Object.freeze({
    identity: 96,
    lore: 460,
    bio: 420,
    listItem: 140,
    abilityName: 64,
    traitName: 64,
  }),
  list: Object.freeze({
    intentions: 8,
    memories: 10,
    inventory: 18,
    activeEffects: 12,
    dormantEffects: 12,
    abilities: 10,
    traits: 10,
  }),
})

function normalizeText(value: unknown, maxLen: number): string {
  const clean = String(value ?? '').replace(/\s+/g, ' ').trim()
  if (!clean) return ''
  if (clean.length <= maxLen) return clean
  return `${clean.slice(0, Math.max(1, maxLen - 1)).trimEnd()}…`
}

function normalizeUniqueList(raw: unknown, maxLen: number): string[] {
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  const seen = new Set<string>()
  for (const entry of raw) {
    const value = normalizeText(entry, maxLen)
    if (!value) continue
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out
}

function clampList(items: string[], limit: number): { items: string[]; overflow: number } {
  const safeLimit = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0
  const overflow = Math.max(0, items.length - safeLimit)
  return { items: items.slice(0, safeLimit), overflow }
}

function dormantToLabel(effect: unknown): string {
  if (effect && typeof effect === 'object') {
    const data = effect as DormantEffect
    return String(data.label || data.source || 'Effect')
  }
  return String(effect ?? '')
}

function clampVital(value: unknown): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.round(n))
}

function normalizeSheetVitals(raw: Partial<Vitals> | undefined): Vitals {
  const maxVitality = Math.max(1, clampVital(raw?.maxVitality ?? raw?.vitality ?? 1))
  const maxSpirit = Math.max(1, clampVital(raw?.maxSpirit ?? raw?.spirit ?? 1))
  const maxEssence = Math.max(1, clampVital(raw?.maxEssence ?? raw?.essence ?? 1))
  return {
    vitality: Math.min(maxVitality, clampVital(raw?.vitality ?? maxVitality)),
    spirit: Math.min(maxSpirit, clampVital(raw?.spirit ?? maxSpirit)),
    essence: Math.min(maxEssence, clampVital(raw?.essence ?? maxEssence)),
    maxVitality,
    maxSpirit,
    maxEssence,
  }
}

export function normalizeNpcSheet(raw: unknown): NpcSheetData {
  const src = (raw && typeof raw === 'object') ? (raw as Partial<NpcSheetData>) : {}
  const upstreamOverflow = (src.overflow && typeof src.overflow === 'object') ? src.overflow : {}
  const rel = Number.isFinite(src.relationship) ? Number(src.relationship) : 0
  const tone = src.relationshipTone === 'friendly' || src.relationshipTone === 'hostile'
    ? src.relationshipTone
    : 'neutral'

  const intentionsRaw = normalizeUniqueList(src.intentions, SHEET_LIMITS.text.listItem)
  const memoriesRaw = normalizeUniqueList(src.memories, SHEET_LIMITS.text.listItem)
  const inventoryRaw = normalizeUniqueList(src.inventory, SHEET_LIMITS.text.listItem)
  const activeEffectsRaw = normalizeUniqueList(src.activeEffects, SHEET_LIMITS.text.listItem)
  const dormantEffectsRaw = normalizeUniqueList(
    Array.isArray(src.dormantEffects) ? src.dormantEffects.map(dormantToLabel) : [],
    SHEET_LIMITS.text.listItem,
  )

  const intentions = clampList(intentionsRaw, SHEET_LIMITS.list.intentions)
  const memories = clampList(memoriesRaw, SHEET_LIMITS.list.memories)
  const inventory = clampList(inventoryRaw, SHEET_LIMITS.list.inventory)
  const activeEffects = clampList(activeEffectsRaw, SHEET_LIMITS.list.activeEffects)
  const dormantEffects = clampList(dormantEffectsRaw, SHEET_LIMITS.list.dormantEffects)

  return {
    identity: normalizeText(src.identity, SHEET_LIMITS.text.identity),
    lore: normalizeText(src.lore, SHEET_LIMITS.text.lore),
    sex: normalizeText(src.sex, 16),
    relationship: rel,
    relationshipLabel: normalizeText(src.relationshipLabel, 24) || 'NEUTRAL',
    relationshipTone: tone,
    intentions: intentions.items,
    memories: memories.items,
    inventory: inventory.items,
    activeEffects: activeEffects.items,
    dormantEffects: dormantEffects.items,
    overflow: {
      intentions: intentions.overflow + Math.max(0, Number(upstreamOverflow.intentions ?? 0) || 0),
      memories: memories.overflow + Math.max(0, Number(upstreamOverflow.memories ?? 0) || 0),
      inventory: inventory.overflow + Math.max(0, Number(upstreamOverflow.inventory ?? 0) || 0),
      activeEffects: activeEffects.overflow + Math.max(0, Number(upstreamOverflow.activeEffects ?? 0) || 0),
      dormantEffects: dormantEffects.overflow + Math.max(0, Number(upstreamOverflow.dormantEffects ?? 0) || 0),
    },
  }
}

export function normalizeCompanionSheet(raw: unknown): CompanionSheetData {
  const src = (raw && typeof raw === 'object') ? (raw as Partial<CompanionSheetData>) : {}
  const upstreamOverflow = (src.overflow && typeof src.overflow === 'object') ? src.overflow : {}
  const abilitiesRaw = Array.isArray(src.abilities) ? src.abilities : []
  const traitsRaw = Array.isArray(src.traits) ? src.traits : []

  const abilityCards = abilitiesRaw
    .map((ability) => {
      const name = normalizeText(ability?.name, SHEET_LIMITS.text.abilityName)
      if (!name) return null
      return {
        name,
        desc: normalizeText(ability?.desc, SHEET_LIMITS.text.listItem),
        cooldown: Number.isFinite(ability?.cooldown) ? Number(ability?.cooldown) : undefined,
      }
    })
    .filter(Boolean) as CompanionSheetData['abilities']

  const traitCards = traitsRaw
    .map((trait) => {
      const name = normalizeText(trait?.name, SHEET_LIMITS.text.traitName)
      if (!name) return null
      return {
        name,
        desc: normalizeText(trait?.desc, SHEET_LIMITS.text.listItem),
      }
    })
    .filter(Boolean) as CompanionSheetData['traits']

  const abilityOverflow = Math.max(0, abilityCards.length - SHEET_LIMITS.list.abilities)
  const traitOverflow = Math.max(0, traitCards.length - SHEET_LIMITS.list.traits)
  const inventory = clampList(
    normalizeUniqueList(src.inventory, SHEET_LIMITS.text.listItem),
    SHEET_LIMITS.list.inventory,
  )
  const activeEffects = clampList(
    normalizeUniqueList(src.activeEffects, SHEET_LIMITS.text.listItem),
    SHEET_LIMITS.list.activeEffects,
  )
  const dormantEffects = clampList(
    normalizeUniqueList(src.dormantEffects, SHEET_LIMITS.text.listItem),
    SHEET_LIMITS.list.dormantEffects,
  )

  return {
    identity: normalizeText(src.identity, SHEET_LIMITS.text.identity),
    bio: normalizeText(src.bio, SHEET_LIMITS.text.bio),
    vitals: normalizeSheetVitals(src.vitals),
    abilities: abilityCards.slice(0, SHEET_LIMITS.list.abilities),
    traits: traitCards.slice(0, SHEET_LIMITS.list.traits),
    inventory: inventory.items,
    activeEffects: activeEffects.items,
    dormantEffects: dormantEffects.items,
    overflow: {
      abilities: abilityOverflow + Math.max(0, Number(upstreamOverflow.abilities ?? 0) || 0),
      traits: traitOverflow + Math.max(0, Number(upstreamOverflow.traits ?? 0) || 0),
      inventory: inventory.overflow + Math.max(0, Number(upstreamOverflow.inventory ?? 0) || 0),
      activeEffects: activeEffects.overflow + Math.max(0, Number(upstreamOverflow.activeEffects ?? 0) || 0),
      dormantEffects: dormantEffects.overflow + Math.max(0, Number(upstreamOverflow.dormantEffects ?? 0) || 0),
    },
  }
}
